import React, { useState, useEffect, useContext } from 'react';
import { Button, Card, Form, Input, Modal, Space, Tabs, Col } from 'antd';
import OtpInput from 'react-otp-input';
import styles from './profile-change-info.module.scss';
import './my-profile.scss';

import { AppContext } from '@layouts/AppRouter';
import { getUserRoleByRoles } from '@services/utils';
import { toast } from 'react-toastify';
import api from '@services/api';
import { ApiError, User, SuperGroup } from '@services/types';
import { Role } from '@openapi';
import MfaSwitch from 'component/MfaSwitch';

const { TabPane } = Tabs;

const MyProfile = () => {
  const [form] = Form.useForm<User>();
  const [superGroups, setSuperGroups] = useState<SuperGroup[]>([]);
  const { user, setUser, variablesProfile } = useContext(AppContext);
  const isSaml = variablesProfile?.profile === 'saml';

  const [activeTab, setActiveTab] = useState('info');

  const [mfaEnabled, setMfaEnabled] = useState<boolean>(!!user?.mfaEnabled);
  const [qrCodeUri, setQrCodeUri] = useState<string | undefined>('');
  const [otp, setOtp] = useState<string>('');
  const [showVerify, setShowVerify] = useState<boolean>(false);
  const [disableVisible, setDisableVisible] = useState(false);

  const handleTabChange = (key: string) => setActiveTab(key);

  useEffect(() => {
    api.administration.getSuperGroups().then(setSuperGroups);
  }, []);

  const handleSubmit = async () => {
    try {
      if (!user) throw new Error('User is not available');

      const values = await form.validateFields();

      await api.administration.updateUser(user.id, {
        ...values,
        role: getUserRoleByRoles(user.roles || []) || Role.Superadmin,
        email: user.email,
        superGroupIds: superGroups.map((sg) => sg.id),
      });
      toast.success('User was updated successfully');
      const newUser = await api.auth.me();
      setUser(newUser);
      form.resetFields(['password', 'confirm']);
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(`${error.error.details}`);
      } else {
        toast.error('User was not updated due to error');
      }
    }
  };

  const handleDisable = async () => {
    try {
      await api.mfa.disable({ code: parseInt(otp, 10) });
      setMfaEnabled(false);
      setQrCodeUri('');
      setOtp('');
      setDisableVisible(false);
      toast.success('MFA disabled');
    } catch (err) {
      toast.error('Failed to disable MFA');
    }
  };

  const handleMfaToggle = async (checked: boolean) => {
    try {
      setOtp('');
      if (checked) {
        const res = await api.mfa.enable();

        setQrCodeUri(res.qrCodeUri);
        setShowVerify(true);
        toast.info('Scan the QR code with Google Authenticator and verify');
      } else {
        setDisableVisible(true);
      }
    } catch (err) {
      toast.error('Failed to toggle MFA');
      console.error(err);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const res = await api.mfa.verify({ code: parseInt(otp, 10) });
      if ('message' in res) {
        toast.success(res.message);
        setMfaEnabled(true);
        setShowVerify(false);
        setQrCodeUri('');
        setOtp('');
      } else if ('error' in res) {
        toast.error(res.error);
      }
    } catch (err) {
      toast.error('Verification failed');
    }
  };

  return (
    <Card
      title="My Profile"
      extra={
        <Space>
          <Button type="primary" onClick={handleSubmit}>
            Modify
          </Button>
        </Space>
      }
    >
      <div className="main">
        <div className="modal-profile-window">
          <div className="modal-container">
            <div className="role-block">
              <div className="user-info">
                <span className="title">My Email</span>
                <span>{user?.email}</span>
              </div>
              <div className="user-info">
                <span className="title">Role</span>
                <span>{getUserRoleByRoles(user?.roles || [])}</span>
              </div>
              <div className="user-info">
                <span className="title">Perimeter</span>
                <span>{user?.etc?.perimeterNames?.join(', ')}</span>
              </div>
            </div>

            <Tabs
              onChange={handleTabChange}
              style={{ marginTop: '10px' }}
              type="card"
              size="large"
              defaultActiveKey="info"
            >
              <TabPane tab={user ? 'Info' : 'Add Info'} key="info">
                <Form
                  className={styles.form}
                  layout="vertical"
                  form={form}
                  name="modify"
                  initialValues={{
                    firstName: user?.firstName || '',
                    lastName: user?.lastName || '',
                  }}
                  scrollToFirstError
                >
                  <Form.Item
                    className={styles.block}
                    style={{ gridArea: 'firstName' }}
                    label="First Name"
                    name="firstName"
                  >
                    <Input className={styles.item} placeholder="First Name" />
                  </Form.Item>
                  <Form.Item
                    className={styles.block}
                    style={{ gridArea: 'lastName' }}
                    label="Last Name"
                    name="lastName"
                  >
                    <Input className={styles.item} placeholder="Last Name" />
                  </Form.Item>
                </Form>
              </TabPane>

              <TabPane tab="Security" key="security">
                {activeTab === 'security' && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px',
                    }}
                  >
                    <Form
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gridGap: '35px',
                      }}
                      form={form}
                      layout="vertical"
                      name="modify"
                      initialValues={{ password: '' }}
                    >
                      <Form.Item
                        name="password"
                        label="New Password"
                        rules={[
                          {
                            required: true,
                            message: 'Please input your password!',
                          },
                        ]}
                        hasFeedback
                      >
                        <Input.Password />
                      </Form.Item>
                      <Form.Item
                        name="confirm"
                        label="Confirm Password"
                        dependencies={['password']}
                        hasFeedback
                        rules={[
                          {
                            required: true,
                            message: 'Please confirm your password!',
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('Passwords do not match!'));
                            },
                          }),
                        ]}
                      >
                        <Input.Password />
                      </Form.Item>
                    </Form>

                    {/* MFA Switch for superadmin only*/}
                    {!isSaml && user?.roles.includes(Role.Superadmin) && (
                      <MfaSwitch checked={mfaEnabled} onChange={handleMfaToggle} />
                    )}

                    {!isSaml && qrCodeUri && (
                      <div style={{ textAlign: 'center' }}>
                        <p>Scan this QR code in Google Authenticator</p>
                        <img src={qrCodeUri} alt="MFA QR Code" />
                      </div>
                    )}

                    {!isSaml && showVerify && (
                      <Col style={{ textAlign: 'center' }}>
                        <p>Enter the 6-digit code from your app</p>
                        <OtpInput
                          value={otp}
                          onChange={setOtp}
                          numInputs={6}
                          renderInput={(props) => <input {...props} />}
                          inputStyle={{
                            width: '2rem',
                            height: '2rem',
                            margin: '0 0.2rem',
                            fontSize: '1.5rem',
                            borderRadius: 4,
                            border: '1px solid #ccc',
                          }}
                          containerStyle={{
                            justifyContent: 'center',
                          }}
                        />
                        <div style={{ marginTop: 15 }}>
                          <Button type="primary" onClick={handleVerifyOtp} disabled={otp.length !== 6}>
                            Verify
                          </Button>
                        </div>
                      </Col>
                    )}
                  </div>
                )}
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>

      {!isSaml && (
        <Modal
          title="Disable MFA"
          visible={disableVisible}
          onOk={handleDisable}
          onCancel={() => setDisableVisible(false)}
        >
          <p>Enter your MFA code to disable:</p>
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            renderInput={(props) => <input {...props} />}
            inputStyle={{
              width: '2rem',
              height: '2rem',
              margin: '0 0.2rem',
              fontSize: '1.5rem',
              borderRadius: 4,
              border: '1px solid #ccc',
            }}
          />
        </Modal>
      )}
    </Card>
  );
};

export default React.memo(MyProfile);
